import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Modal, Form, Input, Popconfirm, Radio } from 'antd';

import { GroupList } from '@/features/constructorCommon/components/GroupList';
import {
  Divider,
  NestedBox,
  ActionBox,
  Space,
  Box,
  AnswerItem
} from '@/features/constructorCommon/styled';
import { ProductDependedAnswers, ProductGroup } from '@/features/constructorProducts';
import { QuestionnaireDependedAnswers, QuestionnaireGroup } from '@/features/constructor';
import { OPERATORS } from '@/utils/static';
import { ConstructorForm } from '@/components/admin/molecules';

export const Section = ({
  sectionProp,
  onCreated,
  handleCreate,
  handleUpdate,
  handleDelete,
  isCreating,
  isUpdating,
  isDeleting,
  type,
  dataSortedSections,
  loadingSections,
  dependentAnswers,
  setDependentAnswers,
  ...props
}) => {
  const { t } = useTranslation();
  const [section, setSection] = useState(sectionProp || {});
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [isDepends, setIsDepends] = useState(false);
  const [dAnswers, setDAnswers] = useState([]);
  const [isModalOpened, setIsModalOpened] = useState(false);

  useEffect(() => {
    if (!sectionProp?.id || isModalOpened) return;

    setSection(sectionProp);
    if (sectionProp?.visibleConditions && sectionProp?.visibleConditions.length > 0) {
      setIsDepends(true);
    }
  }, [sectionProp]);

  useEffect(() => {
    if (!dataSortedSections || !dependentAnswers || !dependentAnswers.length) return;

    setDAnswers(dependentAnswers);
  }, [dataSortedSections, dependentAnswers]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setSection({ ...section, [name]: value });
  };

  const onChangeOperator = ({ target: { value } }) => {
    setSection({ ...section, visibleOperator: value });
  };

  const handleAction = () => {
    const dAnswersProp = isDepends
      ? dAnswers.length > 0
        ? dAnswers
        : !!sectionProp?.visibleConditions?.length
          ? null
          : []
      : [];

    if (section.id) {
      handleUpdate({
        section: section,
        ...(type === 'questionnaire' && { dAnswersProp: dAnswersProp })
      });
    } else {
      handleCreate({
        section: section,
        ...(type === 'questionnaire' && { dAnswersProp: dAnswersProp })
      }).then(() => {
        setSection({});
        onCreated && onCreated();
      });
    }
  };

  const onAddAnswers = (ans) => {
    setDAnswers(ans);

    setIsModalOpened(false);
  };

  const onDelete = () => {
    handleDelete(section);
  };

  const isDisabled =
    !section.section ||
    (section.section === sectionProp?.section &&
      section.section_RU === sectionProp?.section_RU &&
      section.section_LV === sectionProp?.section_LV &&
      section.newNumber === sectionProp?.newNumber &&
      section.sortOrder === sectionProp?.sortOrder &&
      dAnswers.length === 0 &&
      !!sectionProp?.visibleConditions?.length === isDepends &&
      section.visibleOperator === sectionProp?.visibleOperator);

  return (
    <Box
      $isNew={!section.id}
      styles={{
        body: {
          padding: '16px'
        }
      }}
    >
      <ConstructorForm>
        <Form.Item label={t('Section name')}>
          <Input name='section' value={section.section} onChange={onChange} />
        </Form.Item>
        {type === 'products' && (
          <>
            <Form.Item label={`${t('Section name')} (RU)`}>
              <Input name='section_RU' value={section.section_RU} onChange={onChange} />
            </Form.Item>
            <Form.Item label={`${t('Section name')} (LV)`}>
              <Input name='section_LV' value={section.section_LV} onChange={onChange} />
            </Form.Item>
          </>
        )}
        {type === 'questionnaire' && (
          <>
            <Form.Item>
              <Checkbox
                name='isDepends'
                value={isDepends}
                checked={isDepends}
                onChange={(e) => {
                  setIsDepends(e.target.checked);
                  if (!e.target.checked) setDAnswers([]);
                }}
              >
                {t('This section depends on the answers')}
              </Checkbox>
            </Form.Item>

            {isDepends && (
              <>
                <div>
                  {dAnswers.map((item, index) => (
                    <AnswerItem key={`sectionDependentAnswer${index}`}>{item.answer}</AnswerItem>
                  ))}
                </div>
                <Button onClick={() => setIsModalOpened(true)} type='primary'>
                  {section?.visibleConditions?.length
                    ? t('Update dependent Answers')
                    : t('Select dependent Answers')}
                </Button>

                <Space />
                <Radio.Group
                  options={OPERATORS}
                  onChange={onChangeOperator}
                  value={section.visibleOperator}
                />
                <Space />

                <Modal
                  title={t('Select dependent Answers')}
                  width={700}
                  style={{
                    top: 20
                  }}
                  okText={'Add'}
                  footer={null}
                  open={isModalOpened}
                  // onOk={onAddAnswers}
                  onCancel={() => setIsModalOpened(false)}
                >
                  {type === 'questionnaire' && (
                    <QuestionnaireDependedAnswers
                      dataSortedSections={dataSortedSections}
                      loadingSections={loadingSections}
                      dependentAnswers={dependentAnswers}
                      setDependentAnswers={setDependentAnswers}
                      onAddAnswers={onAddAnswers}
                    />
                  )}
                  {type === 'products' && (
                    <ProductDependedAnswers
                      dataSortedSections={dataSortedSections}
                      loadingSections={loadingSections}
                      dependentAnswers={dependentAnswers}
                      setDependentAnswers={setDependentAnswers}
                      onAddAnswers={onAddAnswers}
                    />
                  )}
                </Modal>
              </>
            )}
          </>
        )}
        {section.id && (
          <>
            <Form.Item label={t('Section order')}>
              <Input
                name={type === 'questionnaire' ? 'newNumber' : 'sortOrder'}
                value={type === 'questionnaire' ? section.newNumber : section.sortOrder}
                style={{ width: '100px' }}
                onChange={onChange}
              />
            </Form.Item>
          </>
        )}
        <ActionBox>
          <Button
            onClick={handleAction}
            disabled={isDisabled}
            type='primary'
            loading={isCreating || isUpdating}
          >
            {section.id ? t('Update Section') : t('Create Section')}
          </Button>

          {section.id && (
            <Popconfirm
              placement='topRight'
              title={t('Are you sure to delete this section?')}
              onConfirm={onDelete}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <Button danger loading={isDeleting}>
                {t('Delete Section')}
              </Button>
            </Popconfirm>
          )}
        </ActionBox>
      </ConstructorForm>

      <NestedBox>
        {section.id && <Divider />}
        {type === 'questionnaire' && !!section.questionGroups?.length && (
          <>
            <GroupList list={section.questionGroups} type={type} {...props} />
          </>
        )}
        {type === 'products' && !!section.productGroups?.length && (
          <>
            <GroupList list={section.productGroups} type={type} />
          </>
        )}

        {section.id && (
          <>
            {showCreateNew ? (
              <>
                {type === 'questionnaire' && (
                  <QuestionnaireGroup
                    sectionId={section.id}
                    onCreated={() => setShowCreateNew(false)}
                    {...props}
                  />
                )}
                {type === 'products' && (
                  <ProductGroup sectionId={section.id} onCreated={() => setShowCreateNew(false)} />
                )}
              </>
            ) : (
              <Button onClick={() => setShowCreateNew(true)}>{t('Add new Group')}</Button>
            )}
          </>
        )}
      </NestedBox>
    </Box>
  );
};
